import api from '@/services/api';
import type { PeriodStatementResult, StatementPeriod } from '../types/dealerFundStatementPeriod.types';
import type { FundAccountPosting, FundAccountPostingsPage } from '../types/fundAccountPostings.types';
import { periodQueryParams } from '../utils/statementPeriod';
import { assertWindow, readBoundedNullable, readBoundedText, readMatchingId, readUuid } from './periodPayloadChecks';
import { hasApiCode, NOT_ENTITLED_CODE, payloadReaders, UNVERIFIED_CODE } from './statementPayloadReaders';
import { vatPayloadReaders } from './vatPayloadReaders';

export const FUND_ACCOUNT_POSTINGS_PER_PAGE = 50;

const TRANCODE = /^[A-Z0-9]{1,20}$/;
const CURRENCY = 'GBP';
const SCALE = 2;
const INVOICE_NUMBER_MAX = 50;
const DESCRIPTION_MAX = 255;

const { malformed, readRecord, readMoney, readInt, readRealDate, readArray } =
    payloadReaders('fund account postings payload');

const { readLineVat, readPostingSplit, readRowCount } = vatPayloadReaders('fund account postings payload');

const requireKeys = (raw: Record<string, unknown>, keys: string[]): void => {
    keys.forEach((key) => {
        if (raw[key] === undefined) throw malformed(`${key} is missing`);
    });
};

const mapPosting = (value: unknown, from: string, to: string): FundAccountPosting => {
    const raw = readRecord(value, 'row');
    requireKeys(raw, ['invoice_id', 'invoice_number', 'description']);
    const effectiveDate = readRealDate(raw.effective_date, 'effective_date');
    if (effectiveDate < from || effectiveDate > to) throw malformed('effective_date is outside the window');
    const amount = readMoney(raw.amount, 'amount');
    return {
        agreementId: readUuid(raw.agreement_id, 'agreement_id'),
        effectiveDate,
        amount,
        invoiceId: raw.invoice_id === null ? null : readUuid(raw.invoice_id, 'invoice_id'),
        invoiceNumber: readBoundedNullable(raw.invoice_number, 'invoice_number', INVOICE_NUMBER_MAX),
        description: readBoundedNullable(raw.description, 'description', DESCRIPTION_MAX),
        ...readPostingSplit(raw, amount),
    };
};

const lastPageFor = (total: number, perPage: number): number => Math.max(1, Math.ceil(total / perPage));

const expectedRowCount = (total: number, page: number, perPage: number): number =>
    Math.min(perPage, Math.max(0, total - (page - 1) * perPage));

const mapPostingsPage = (
    data: unknown,
    sellingDealerId: string,
    productId: string,
    trancode: string,
    period: StatementPeriod,
    page: number,
): FundAccountPostingsPage => {
    const raw = readRecord(data, 'root');
    if (raw.layer !== 'pair') throw malformed('layer is not pair');
    readMatchingId(raw.dealer_id, 'dealer_id', sellingDealerId);
    readMatchingId(raw.product_id, 'product_id', productId);
    if (raw.trancode !== trancode || !TRANCODE.test(trancode)) throw malformed('trancode does not match the request');
    if (raw.scale !== SCALE) throw malformed('scale is not 2');
    if (raw.currency !== CURRENCY) throw malformed('currency is not GBP');
    if (raw.check !== 'verified') throw malformed('check is not verified');
    readBoundedText(raw.rounding_mode, 'rounding_mode', 32);
    requireKeys(raw, ['name']);
    const from = readRealDate(raw.from, 'from');
    const to = readRealDate(raw.to, 'to');
    assertWindow(period, from, to);

    const postings = readInt(raw.postings, 'postings', 0);
    const amount = readMoney(raw.amount, 'amount');
    if (postings === 0 && amount !== '0.00') throw malformed('amount is not zero for an empty line');

    const meta = readRecord(raw.rows_page, 'rows_page');
    const total = readInt(meta.total, 'rows_page total', 0);
    const lastPage = readInt(meta.last_page, 'rows_page last_page', 1);
    if (meta.page !== page || meta.per_page !== FUND_ACCOUNT_POSTINGS_PER_PAGE) {
        throw malformed('rows_page does not match the request');
    }
    if (total !== postings) throw malformed('rows_page total does not match postings');
    if (lastPage !== lastPageFor(total, FUND_ACCOUNT_POSTINGS_PER_PAGE)) {
        throw malformed('rows_page last_page is inconsistent');
    }

    const rows = readArray(raw.rows, 'rows');
    if (rows.length !== expectedRowCount(total, page, FUND_ACCOUNT_POSTINGS_PER_PAGE)) {
        throw malformed('rows does not match rows_page');
    }
    const postingRows = rows.map((row) => mapPosting(row, from, to));
    return {
        trancode,
        name: readBoundedNullable(raw.name, 'name', 255),
        currency: CURRENCY,
        from,
        to,
        amount,
        postings,
        ...readLineVat(raw, amount, postings, 'line'),
        rowCount: readRowCount(raw.row_count, postingRows),
        rows: postingRows,
        page,
        perPage: FUND_ACCOUNT_POSTINGS_PER_PAGE,
        total,
        lastPage,
    };
};

export const fetchFundAccountPostings = async (
    sellingDealerId: string,
    productId: string,
    trancode: string,
    period: StatementPeriod,
    page: number,
): Promise<PeriodStatementResult<FundAccountPostingsPage>> => {
    try {
        const response = await api.get(
            `/dealers/${sellingDealerId}/fund-statement/periods/${productId}/rows/${trancode}`,
            {
                params: { ...periodQueryParams(period), page, per_page: FUND_ACCOUNT_POSTINGS_PER_PAGE },
                suppressGlobalForbiddenToast: true,
            },
        );
        return {
            status: 'ok',
            statement: mapPostingsPage(response.data?.data, sellingDealerId, productId, trancode, period, page),
        };
    } catch (error) {
        if (hasApiCode(error, 503, UNVERIFIED_CODE)) return { status: 'unverified' };
        if (hasApiCode(error, 403, NOT_ENTITLED_CODE)) return { status: 'not_entitled' };
        throw error;
    }
};
